/**
 * Composable para envío del formulario de emisión: Update ("Me interesa") y flujo de emisión.
 * Orquesta los módulos de acciones y expone estado/handlers para la UI.
 */

import { ref } from "vue";
import { useQuoteStore } from "@/stores/quoteStore";
import { useEmail } from "@/composables/useEmail";
import type {
  Documento,
  EmissionFormData,
  EmissionSuccessPayload,
  PolizaEmitida,
} from "@/types/emission.types";
import type {
  EmailBackgroundParams,
  EmissionSubmitStateRefs,
  EmissionSubmitDependencies,
  EmissionSubmitProps,
} from "@/composables/emission-submit/contracts";
import { maintainExpandMode } from "@/composables/emission-submit/helpers";
import {
  downloadDocument,
  executeEmitPolicyFlow,
  loadDocuments,
} from "@/composables/emission-submit/emit-policy";
import {
  executeDownloadOffer,
  executeSendOffer,
} from "@/composables/emission-submit/offer-actions";
import { executeUpdateQuoteFlow } from "@/composables/emission-submit/update-quote";

export type { EmissionSubmitProps };

export function useEmissionSubmit(
  formData: EmissionFormData,
  getProps: () => EmissionSubmitProps,
  emitSuccess: (data: EmissionSuccessPayload) => void,
) {
  const quoteStore = useQuoteStore();
  const { sendQuoteEmail } = useEmail();

  const state: EmissionSubmitStateRefs = {
    isEmitting: ref(false),
    emissionSuccess: ref(false),
    polizaEmitida: ref<PolizaEmitida | null>(null),
    documentosDisponibles: ref<Documento[]>([]),
    isLoadingDocumentos: ref(false),
    showPrivacyError: ref(false),
    isDownloadingOffer: ref(false),
    isSendingOffer: ref(false),
  };

  const sendEmailInBackground = async (
    params: EmailBackgroundParams,
  ): Promise<void> => {
    const quote = quoteStore.quoteData;
    const email = quote.personalData?.email;
    if (!email || !params.idCotizacion) return;

    try {
      await sendQuoteEmail({
        idCotizacion: params.idCotizacion,
        email,
        telefono: quote.personalData?.whatsapp,
        nombre: quote.personalData?.fullName,
        prima: getProps().quotesResponse?.Cobertura?.PrimaTotal ?? 0,
        fechaVigencia:
          quote.cotizacionData?.finVigencia ??
          quote.cotizacionData?.inicioVigencia,
        quoteData: quote,
      });
    } catch (error) {
      console.warn("⚠️ No se pudo enviar el correo automático:", error);
    }
  };

  const dependencies: EmissionSubmitDependencies = {
    formData,
    getProps,
    emitSuccess,
    quoteStore,
    sendQuoteEmail,
    sendEmailInBackground,
    state,
  };

  const handleUpdateQuote = (): Promise<void> => executeUpdateQuoteFlow(dependencies);
  const handleEmitPolicy = (): Promise<void> => executeEmitPolicyFlow(dependencies);
  const handleDownloadOffer = (): Promise<void> => executeDownloadOffer(dependencies);
  const handleSendOffer = (): Promise<void> => executeSendOffer(dependencies);
  const obtenerDocumentos = (idSolicitud: number): Promise<void> =>
    loadDocuments(dependencies, idSolicitud);
  const descargarDocumento = (guidDocumento: string, nombre?: string): void =>
    downloadDocument(guidDocumento, nombre);

  return {
    isEmitting: state.isEmitting,
    emissionSuccess: state.emissionSuccess,
    polizaEmitida: state.polizaEmitida,
    documentosDisponibles: state.documentosDisponibles,
    isLoadingDocumentos: state.isLoadingDocumentos,
    showPrivacyError: state.showPrivacyError,
    isDownloadingOffer: state.isDownloadingOffer,
    isSendingOffer: state.isSendingOffer,
    handleUpdateQuote,
    handleEmitPolicy,
    handleDownloadOffer,
    handleSendOffer,
    obtenerDocumentos,
    descargarDocumento,
    maintainExpandMode,
  };
}
